import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import esignService from '../../../services/esign.service';
import type { SignatureEnvelope, SignatureAuditEvent } from '../../../types/esign';
import {
  ENVELOPE_STATUS_LABELS,
  ENVELOPE_STATUS_COLORS,
  SIGNER_STATUS_LABELS,
} from '../../../types/esign';

const colorMap: Record<string, string> = {
  gray: 'bg-gray-100 text-gray-700',
  blue: 'bg-blue-50 text-blue-700',
  yellow: 'bg-yellow-50 text-yellow-700',
  green: 'bg-green-50 text-green-700',
  red: 'bg-red-50 text-red-700',
  orange: 'bg-orange-50 text-orange-700',
};

const signerColorMap: Record<string, string> = {
  SIGNER_PENDING: 'bg-gray-100 text-gray-600',
  SIGNER_SENT: 'bg-blue-50 text-blue-700',
  SIGNER_DELIVERED: 'bg-blue-50 text-blue-700',
  SIGNER_VIEWED: 'bg-yellow-50 text-yellow-700',
  SIGNER_SIGNED: 'bg-green-50 text-green-700',
  SIGNER_DECLINED: 'bg-red-50 text-red-700',
};

type TabId = 'overview' | 'signers' | 'audit';

export default function EnvelopeDetailPage() {
  const { envelopeId } = useParams<{ envelopeId: string }>();
  const navigate = useNavigate();

  const [envelope, setEnvelope] = useState<SignatureEnvelope | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  // Audit trail
  const [auditEvents, setAuditEvents] = useState<SignatureAuditEvent[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditIntegrity, setAuditIntegrity] = useState<{ valid: boolean; eventCount: number } | null>(null);

  // Actions
  const [sending, setSending] = useState(false);
  const [reminding, setReminding] = useState(false);

  // Void modal
  const [showVoid, setShowVoid] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voidConfirmText, setVoidConfirmText] = useState('');
  const [voiding, setVoiding] = useState(false);

  const loadEnvelope = useCallback(async () => {
    if (!envelopeId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await esignService.getEnvelope(envelopeId);
      setEnvelope(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load envelope');
    } finally {
      setLoading(false);
    }
  }, [envelopeId]);

  useEffect(() => {
    loadEnvelope();
  }, [loadEnvelope]);

  const loadAuditTrail = useCallback(async () => {
    if (!envelopeId) return;
    setAuditLoading(true);
    try {
      const [trail, integrity] = await Promise.all([
        esignService.getAuditTrail(envelopeId),
        esignService.verifyAuditTrail(envelopeId),
      ]);
      setAuditEvents(trail.events);
      setAuditIntegrity(integrity);
    } catch (err) {
      console.error('Failed to load audit trail:', err);
    } finally {
      setAuditLoading(false);
    }
  }, [envelopeId]);

  useEffect(() => {
    if (activeTab === 'audit' && auditEvents.length === 0) {
      loadAuditTrail();
    }
  }, [activeTab, auditEvents.length, loadAuditTrail]);

  const handleSend = async () => {
    if (!envelopeId) return;
    setSending(true);
    try {
      await esignService.sendEnvelope(envelopeId);
      await loadEnvelope();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send envelope');
    } finally {
      setSending(false);
    }
  };

  const handleRemind = async (signerId?: string) => {
    if (!envelopeId) return;
    setReminding(true);
    try {
      await esignService.sendReminder(envelopeId, signerId);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reminder');
    } finally {
      setReminding(false);
    }
  };

  const handleVoid = async () => {
    if (!envelopeId || voidConfirmText.toLowerCase() !== 'proceed' || !voidReason.trim()) return;
    setVoiding(true);
    try {
      await esignService.voidEnvelope(envelopeId, voidReason.trim());
      setShowVoid(false);
      setVoidReason('');
      setVoidConfirmText('');
      await loadEnvelope();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to void envelope');
    } finally {
      setVoiding(false);
    }
  };

  const handleDownload = async (type: 'original' | 'signed' | 'certificate') => {
    if (!envelopeId || !envelope) return;
    try {
      const blob = await esignService.downloadDocument(envelopeId, type);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${envelope.subject.replace(/\s+/g, '_')}_${type}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download document');
    }
  };

  // Loading
  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="h-96 bg-gray-200 rounded-xl" />
      </div>
    );
  }

  // Error / Not found
  if (error && !envelope) {
    return (
      <div className="space-y-6">
        <Link
          to="/admin/esign/envelopes"
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Envelopes
        </Link>
        <div className="bg-white rounded-lg border p-8 text-center">
          <h3 className="text-lg font-medium text-gray-900">{error}</h3>
          <button
            onClick={() => navigate('/admin/esign/envelopes')}
            className="mt-4 px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded hover:bg-gray-800"
          >
            Return to Envelopes
          </button>
        </div>
      </div>
    );
  }

  if (!envelope) return null;

  const statusColor = ENVELOPE_STATUS_COLORS[envelope.status] || 'gray';
  const isDraft = envelope.status === 'ENVELOPE_DRAFT';
  const isActive = envelope.status === 'ENVELOPE_SENT' || envelope.status === 'ENVELOPE_PARTIALLY_SIGNED';
  const isCompleted = envelope.status === 'ENVELOPE_COMPLETED';

  const tabs: { id: TabId; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'signers', label: `Signers (${envelope.signers?.length || 0})` },
    { id: 'audit', label: 'Audit Trail' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/admin/esign/envelopes"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 text-gray-500" />
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-semibold text-gray-900">{envelope.subject}</h1>
              <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${colorMap[statusColor] || colorMap.gray}`}>
                {ENVELOPE_STATUS_LABELS[envelope.status] || envelope.status}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-0.5">{envelope.documentName}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isDraft && (
            <button
              onClick={handleSend}
              disabled={sending}
              className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded hover:bg-gray-800 disabled:opacity-50"
            >
              {sending ? 'Sending...' : 'Send Now'}
            </button>
          )}
          {isActive && (
            <>
              <button
                onClick={() => handleRemind()}
                disabled={reminding}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50"
              >
                {reminding ? 'Sending...' : 'Send Reminder'}
              </button>
              <button
                onClick={() => setShowVoid(true)}
                className="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 rounded hover:bg-red-100"
              >
                Void
              </button>
            </>
          )}
          {isCompleted && (
            <button
              onClick={() => handleDownload('signed')}
              className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded hover:bg-gray-800"
            >
              Download Signed
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Quick stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border p-4">
          <p className="text-xs text-gray-500 uppercase">Signers</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{envelope.signers?.length || 0}</p>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <p className="text-xs text-gray-500 uppercase">Signed</p>
          <p className="text-2xl font-bold text-green-600 mt-1">
            {envelope.signers?.filter((s) => s.status === 'SIGNER_SIGNED').length || 0}
          </p>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <p className="text-xs text-gray-500 uppercase">Sent</p>
          <p className="text-sm font-medium text-gray-900 mt-1">
            {envelope.sentAt ? new Date(envelope.sentAt).toLocaleDateString() : 'Not sent'}
          </p>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <p className="text-xs text-gray-500 uppercase">Expires</p>
          <p className="text-sm font-medium text-gray-900 mt-1">
            {envelope.expiresAt ? new Date(envelope.expiresAt).toLocaleDateString() : '-'}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-gray-900 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Details */}
          <div className="bg-white rounded-lg border p-6 space-y-4">
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Details</h2>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Subject</span>
                <span className="text-gray-900 font-medium">{envelope.subject}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Document</span>
                <span className="text-gray-900">{envelope.documentName}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Signing Mode</span>
                <span className="text-gray-900">{envelope.signingMode === 'SEQUENTIAL' ? 'Sequential' : 'Parallel'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Pages</span>
                <span className="text-gray-900">{envelope.pageCount}</span>
              </div>
              {envelope.template && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Template</span>
                  <span className="text-gray-900">{envelope.template.name}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Reminders</span>
                <span className="text-gray-900">
                  {envelope.reminderEnabled ? `Every ${envelope.reminderFrequencyDays} days (${envelope.remindersSentCount} sent)` : 'Disabled'}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Created</span>
                <span className="text-gray-900">{new Date(envelope.createdAt).toLocaleString()}</span>
              </div>
              {envelope.completedAt && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Completed</span>
                  <span className="text-gray-900">{new Date(envelope.completedAt).toLocaleString()}</span>
                </div>
              )}
              {envelope.voidedAt && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Voided</span>
                  <span className="text-gray-900">{new Date(envelope.voidedAt).toLocaleString()}</span>
                </div>
              )}
              {envelope.voidedReason && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Void Reason</span>
                  <span className="text-gray-900">{envelope.voidedReason}</span>
                </div>
              )}
            </div>
          </div>

          {/* Message & Documents */}
          <div className="space-y-6">
            {envelope.message && (
              <div className="bg-white rounded-lg border p-6">
                <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">Message to Signers</h2>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{envelope.message}</p>
              </div>
            )}

            <div className="bg-white rounded-lg border p-6">
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">Documents</h2>
              <div className="space-y-2">
                <button
                  onClick={() => handleDownload('original')}
                  className="w-full text-left px-4 py-3 rounded-lg border hover:bg-gray-50 transition-colors"
                >
                  <span className="text-sm font-medium text-gray-900">Original Document</span>
                  <p className="text-xs text-gray-500">{envelope.documentName}</p>
                </button>
                {envelope.signedDocumentPath && (
                  <button
                    onClick={() => handleDownload('signed')}
                    className="w-full text-left px-4 py-3 rounded-lg border hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-sm font-medium text-green-700">Signed Document</span>
                    <p className="text-xs text-gray-500">Completed with all signatures</p>
                  </button>
                )}
                {envelope.certificatePath && (
                  <button
                    onClick={() => handleDownload('certificate')}
                    className="w-full text-left px-4 py-3 rounded-lg border hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-sm font-medium text-blue-700">Audit Certificate</span>
                    <p className="text-xs text-gray-500">Tamper-evident audit trail PDF</p>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'signers' && (
        <div className="bg-white rounded-lg border overflow-hidden overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="bg-gray-50 border-b text-left">
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">#</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Viewed</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Signed</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {envelope.signers?.map((signer) => (
                <tr key={signer.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-600">{signer.signerIndex + 1}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{signer.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{signer.email}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${signerColorMap[signer.status] || signerColorMap.SIGNER_PENDING}`}>
                      {SIGNER_STATUS_LABELS[signer.status] || signer.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {signer.viewedAt ? new Date(signer.viewedAt).toLocaleString() : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {signer.signedAt ? new Date(signer.signedAt).toLocaleString() : '-'}
                  </td>
                  <td className="px-4 py-3">
                    {isActive && signer.status !== 'SIGNER_SIGNED' && signer.status !== 'SIGNER_DECLINED' && (
                      <button
                        onClick={() => handleRemind(signer.id)}
                        disabled={reminding}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
                      >
                        Remind
                      </button>
                    )}
                    {signer.declinedReason && (
                      <span className="text-xs text-red-600" title={signer.declinedReason}>
                        Reason: {signer.declinedReason.slice(0, 50)}{signer.declinedReason.length > 50 ? '...' : ''}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'audit' && (
        <div className="space-y-4">
          {/* Integrity check */}
          {auditIntegrity && (
            <div className={`p-4 rounded-lg border ${auditIntegrity.valid ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${auditIntegrity.valid ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className={`text-sm font-medium ${auditIntegrity.valid ? 'text-green-800' : 'text-red-800'}`}>
                  {auditIntegrity.valid
                    ? `Hash chain intact - ${auditIntegrity.eventCount} events verified`
                    : 'Hash chain integrity compromised!'
                  }
                </span>
              </div>
            </div>
          )}

          {auditLoading ? (
            <div className="text-center py-12 text-gray-500">Loading audit trail...</div>
          ) : auditEvents.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border">
              <p className="text-gray-500">No audit events recorded</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border overflow-hidden overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead>
                  <tr className="bg-gray-50 border-b text-left">
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">#</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Event</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Actor</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">IP</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Time</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Hash</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {auditEvents.map((event) => (
                    <tr key={event.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-xs text-gray-400 font-mono">{event.sequenceNumber}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                          {event.eventType.replace('ESIGN_', '')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {event.actorName || event.actorEmail || event.actorType}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate">{event.description}</td>
                      <td className="px-4 py-3 text-xs text-gray-400 font-mono">{event.ipAddress || '-'}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                        {new Date(event.createdAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-400 font-mono" title={event.eventHash}>
                        {event.eventHash.slice(0, 8)}...
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Void Confirmation Modal */}
      {showVoid && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => { setShowVoid(false); setVoidReason(''); setVoidConfirmText(''); }} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Void Envelope</h2>

            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800 font-medium">This action cannot be undone.</p>
              <p className="text-sm text-red-600 mt-1">
                Voiding &quot;{envelope.subject}&quot; will cancel all pending signatures and notify signers.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason for voiding *</label>
              <textarea
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                placeholder="Explain why this envelope is being voided..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-gray-400 resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type <span className="font-bold">proceed</span> to confirm:
              </label>
              <input
                type="text"
                value={voidConfirmText}
                onChange={(e) => setVoidConfirmText(e.target.value)}
                placeholder="Type 'proceed' to confirm"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>

            <div className="flex gap-3">
              <button
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                onClick={() => { setShowVoid(false); setVoidReason(''); setVoidConfirmText(''); }}
                disabled={voiding}
              >
                Cancel
              </button>
              <button
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                onClick={handleVoid}
                disabled={voiding || voidConfirmText.toLowerCase() !== 'proceed' || !voidReason.trim()}
              >
                {voiding ? 'Voiding...' : 'Void Envelope'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
